import {
  PARTNER_CATEGORIES,
  PARTNER_COUNTIES,
  countyForZip,
  countyLabel,
  formatDateOnly,
  formatE164,
  normalizeLineOfWork,
  partnerCategoryLabel,
  partnerLineOfWork,
  toE164,
  mergeValues,
  partnerDisplayName,
  partnerVcard,
  renderPartnerMessage,
  sendWindowWarning,
  smsSegments,
  whyNotTextable,
} from "@roof-hawk/core";
import { useMemo, useState } from "react";

import { ErrorNote, Modal, Row } from "../../components/ui";
import { navigate, useAsync } from "../../lib/hooks";
import {
  fetchConsentLog,
  fetchPartner,
  fetchPartnerMessages,
  fetchReferrals,
  fetchTemplates,
  insertMessage,
  markSent,
  setConsent,
  signCardImages,
  updatePartner,
  updateReferral,
  type MessageTemplate,
  type Partner,
  type PartnerReferral,
} from "../../lib/partnerQueries";
import { SEND_CHANNEL, send } from "../../lib/sendChannel";
import { useSession } from "../../session";
import { LineOfWorkField } from "./LineOfWorkField";
import { ReferralModal } from "./ReferralModal";

/**
 * One partner: who they are, what has been said to them, and what work has
 * gone each way.
 *
 * Four actions live here and each of them is a thing a rep does between
 * appointments — text them, log a reply that came in, refer somebody, opt them
 * out. The one that is disabled more often than not is Text now, and the
 * tooltip on it always says why: a disabled button with no reason is what
 * people file bugs about.
 */

const when = (iso: string | null): string => {
  if (!iso) return "—";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "—";
  return at.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export function PartnerDetail({
  partnerId,
  network,
}: {
  partnerId: string;
  /**
   * `company_settings.partners_enabled`. Off, this is one card in the card
   * file: who they are, what line of work, the photograph, and Save to
   * Contacts. Texting, consent, referrals and the message log belong to the
   * network and are not shown (ADR 0262).
   */
  network: boolean;
}) {
  const { profileId, displayName } = useSession();
  const partnerQuery = useAsync(() => fetchPartner(partnerId), [partnerId]);
  const messagesQuery = useAsync(
    () => fetchPartnerMessages(partnerId),
    [partnerId],
  );
  const referralsQuery = useAsync(() => fetchReferrals(partnerId), [partnerId]);
  const consentQuery = useAsync(() => fetchConsentLog(partnerId), [partnerId]);
  const templatesQuery = useAsync(fetchTemplates, []);

  const partner = partnerQuery.data;

  const cardsQuery = useAsync(
    () =>
      signCardImages(
        [partner?.card_front_path, partner?.card_back_path].filter(
          (path): path is string => Boolean(path),
        ),
      ),
    [partner?.card_front_path, partner?.card_back_path],
  );

  const [texting, setTexting] = useState(false);
  const [loggingReply, setLoggingReply] = useState(false);
  const [referring, setReferring] = useState<
    "to_partner" | "from_partner" | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [thanking, setThanking] = useState<PartnerReferral | null>(null);
  const [editing, setEditing] = useState(false);

  /*
   * Which referrals have already been thanked for.
   *
   * Read off the message log rather than stored on the referral: a thank-you is
   * a message, the message log is where messages live, and a second boolean
   * that has to be kept in step with it is a second thing to get wrong. The
   * customer's name is the join, because that is what the message says.
   */
  const thankedFor = useMemo(() => {
    const names = (messagesQuery.data ?? [])
      .filter((row) => row.kind === "thank_you")
      .map((row) => row.body);
    const ids = new Set<string>();
    for (const referral of referralsQuery.data ?? []) {
      const name = referral.customer_name?.trim();
      if (name && names.some((body) => body.includes(name)))
        ids.add(referral.id);
    }
    return ids;
  }, [messagesQuery.data, referralsQuery.data]);

  const counts = useMemo(() => {
    const rows = referralsQuery.data ?? [];
    return {
      from: rows.filter((row) => row.direction === "from_partner").length,
      to: rows.filter((row) => row.direction === "to_partner").length,
      won: rows.filter((row) => row.outcome === "won").length,
    };
  }, [referralsQuery.data]);

  if (partnerQuery.error) {
    return (
      <div className="list">
        <div className="empty">
          <b>Could not load</b>
          {partnerQuery.error}
        </div>
      </div>
    );
  }
  if (!partner) return null;

  const blocked = whyNotTextable(partner);

  async function reload(): Promise<void> {
    await Promise.all([
      partnerQuery.reload(),
      messagesQuery.reload(),
      referralsQuery.reload(),
      consentQuery.reload(),
    ]);
  }

  async function optOut(): Promise<void> {
    setError(null);
    try {
      await setConsent({
        partnerId,
        event: "opt_out",
        actorProfileId: profileId,
        note: `Marked by ${displayName} after a reply asking to stop.`,
      });
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function reOptIn(note: string): Promise<void> {
    setError(null);
    try {
      await setConsent({
        partnerId,
        event: partner!.sms_opted_in_at ? "re_opt_in" : "verbal_opt_in",
        actorProfileId: profileId,
        note,
      });
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function togglePause(): Promise<void> {
    setError(null);
    try {
      await updatePartner(partnerId, {
        campaign_paused: !partner!.campaign_paused,
      });
      await partnerQuery.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="partner-detail">
      <div className="listhead">
        <div>
          <h2>{partnerDisplayName(partner)}</h2>
          <div className="sub">
            {[partner.title, partner.company].filter(Boolean).join(" · ") ||
              "No company on the card"}
          </div>
        </div>
        <div className="lh-right">
          <button className="btn-ghost" onClick={() => navigate("partners")}>
            {network ? "Back to partners" : "Back to the cards"}
          </button>
        </div>
      </div>

      <ErrorNote error={error} />

      <div className="partner-actions">
        {network && (
          <>
            <button
              className="btn-primary"
              disabled={Boolean(blocked)}
              title={blocked ?? "Opens Messages with the text ready"}
              onClick={() => setTexting(true)}
            >
              Text now
            </button>
            <button className="btn-ghost" onClick={() => setLoggingReply(true)}>
              Log a reply
            </button>
            <button
              className="btn-ghost"
              onClick={() => setReferring("to_partner")}
            >
              Refer a customer to them
            </button>
            <button
              className="btn-ghost"
              onClick={() => setReferring("from_partner")}
            >
              Log a referral from them
            </button>
          </>
        )}
        <button className="btn-ghost" onClick={() => downloadVcard(partner)}>
          Save to Contacts
        </button>
      </div>

      {network && blocked && (
        <div className="blocked-note">
          <b>Cannot be texted</b>
          {blocked}
        </div>
      )}

      <div className="partner-grid">
        <section className="pcard">
          <div className="pcard-head">
            <h3>Contact</h3>
            <button className="btn-small" onClick={() => setEditing(true)}>
              Edit
            </button>
          </div>
          {editing && (
            <EditContact
              partner={partner}
              network={network}
              onClose={() => setEditing(false)}
              onSaved={async () => {
                setEditing(false);
                await partnerQuery.reload();
              }}
            />
          )}
          <Row label="Mobile">
            {partner.phone_mobile ? (
              <a href={`tel:${partner.phone_mobile}`}>
                {formatE164(partner.phone_mobile)}
              </a>
            ) : network ? (
              <span className="sub">None — cannot receive a text</span>
            ) : (
              "—"
            )}
          </Row>
          <Row label="Office">{formatE164(partner.phone_office) ?? "—"}</Row>
          <Row label="Email">
            {partner.email ? (
              <a href={`mailto:${partner.email}`}>{partner.email}</a>
            ) : (
              "—"
            )}
          </Row>
          <Row label="Website">
            {partner.website ? (
              <a href={partner.website} target="_blank" rel="noreferrer">
                {partner.website}
              </a>
            ) : (
              "—"
            )}
          </Row>
          <Row label="Line of work">{partnerLineOfWork(partner)}</Row>
          {network && (
            <Row label="Category">{partnerCategoryLabel(partner.category)}</Row>
          )}
          <Row label="County">{countyLabel(partner.county)}</Row>
          {network ? (
            <Row label="Owner">
              {partner.owner?.full_name ??
                "Nobody — texts have no phone to leave from"}
            </Row>
          ) : (
            <Row label="Scanned by">{partner.owner?.full_name ?? "—"}</Row>
          )}
          <Row label="Met">
            {/* formatDateOnly, not `when`: met_on is a date column, and
                reading it as an instant shows the day before to anybody west
                of UTC. */}
            {[partner.met_at, formatDateOnly(partner.met_on)]
              .filter(Boolean)
              .join(" · ") || "—"}
          </Row>
          {partner.notes && <Row label="Notes">{partner.notes}</Row>}
        </section>

        {network && (
          <section className="pcard">
            <h3>Consent</h3>
            <Row label="Status">
              {partner.sms_status === "opted_in" && (
                <span className="pill in">Can be texted</span>
              )}
              {partner.sms_status === "no_consent" && (
                <span className="pill ask">Nobody has asked</span>
              )}
              {partner.sms_status === "opted_out" && (
                <span className="pill out">Opted out</span>
              )}
            </Row>
            <Row label="Campaign">
              {partner.campaign_paused ? "Paused" : "Running"}
            </Row>
            <Row label="Last text">{when(partner.last_texted_at)}</Row>
            <Row label="Next monthly due">
              {when(partner.next_monthly_due_at)}
            </Row>

            <div className="consent-actions">
              {partner.sms_status !== "opted_out" ? (
                <button
                  className="btn-small warn"
                  onClick={() => void optOut()}
                >
                  They asked me to stop
                </button>
              ) : (
                <AskAgain onDone={reOptIn} />
              )}
              {partner.sms_status === "opted_in" && (
                <button
                  className="btn-small"
                  onClick={() => void togglePause()}
                >
                  {partner.campaign_paused ? "Unpause" : "Pause the campaign"}
                </button>
              )}
              {partner.sms_status === "no_consent" && (
                <AskAgain onDone={reOptIn} />
              )}
            </div>

            <h4>What was recorded</h4>
            {(consentQuery.data ?? []).length === 0 ? (
              <p className="sub">Nothing yet.</p>
            ) : (
              <ul className="consent-log">
                {(consentQuery.data ?? []).map((row) => (
                  <li key={row.id}>
                    <b>
                      {row.event === "opt_out"
                        ? "Asked to stop"
                        : row.event === "re_opt_in"
                          ? "Said yes again"
                          : "Said yes"}
                    </b>
                    <span className="sub">
                      {when(row.created_at)}
                      {row.actor?.full_name ? ` · ${row.actor.full_name}` : ""}
                    </span>
                    {row.note && <p>{row.note}</p>}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {network && (
          <section className="pcard">
            <h3>Referrals</h3>
            <div className="ref-counts">
              <div>
                <b>{counts.from}</b>
                <span className="k">from them</span>
              </div>
              <div>
                <b>{counts.to}</b>
                <span className="k">to them</span>
              </div>
              <div>
                <b>{counts.won}</b>
                <span className="k">sold</span>
              </div>
            </div>
            {(referralsQuery.data ?? []).length === 0 ? (
              <p className="sub">Nothing either way yet.</p>
            ) : (
              <ul className="ref-list">
                {(referralsQuery.data ?? []).map((row) => (
                  <ReferralRow
                    key={row.id}
                    referral={row}
                    partner={partner}
                    thanked={thankedFor.has(row.id)}
                    onThank={() => setThanking(row)}
                    onOutcome={async (outcome) => {
                      await updateReferral(row.id, { outcome });
                      await referralsQuery.reload();
                    }}
                  />
                ))}
              </ul>
            )}
          </section>
        )}

        <section className="pcard">
          <h3>The card</h3>
          {Object.values(cardsQuery.data ?? {}).length === 0 ? (
            <p className="sub">No photograph — this one was typed in.</p>
          ) : (
            <div className="card-images">
              {Object.values(cardsQuery.data ?? {}).map((url) => (
                <img key={url} src={url} alt="The business card" />
              ))}
            </div>
          )}
        </section>
      </div>

      {network && (
        <section className="pcard wide">
          <h3>Messages</h3>
          {(messagesQuery.data ?? []).length === 0 ? (
            <p className="sub">Nothing has been said yet.</p>
          ) : (
            <ul className="msg-log">
              {(messagesQuery.data ?? []).map((row) => (
                <li key={row.id} className={row.direction}>
                  <div className="msg-meta">
                    <span className={`pill ${row.status}`}>{row.status}</span>
                    <span className="sub">
                      {row.direction === "inbound" ? "They said" : row.kind} ·{" "}
                      {when(row.sent_at ?? row.created_at)}
                    </span>
                  </div>
                  <p>{row.body}</p>
                  {row.skip_reason && (
                    <span className="sub">Skipped: {row.skip_reason}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {texting && (
        <TextNowModal
          partner={partner}
          templates={(templatesQuery.data ?? []).filter(
            (template) => template.active && template.kind !== "storm",
          )}
          repFirstName={displayName.split(" ")[0] ?? ""}
          referralsFromCount={counts.from}
          referralsToCount={counts.to}
          onClose={() => setTexting(false)}
          onSent={async () => {
            setTexting(false);
            await reload();
          }}
        />
      )}

      {loggingReply && (
        <LogReplyModal
          partnerId={partnerId}
          onClose={() => setLoggingReply(false)}
          onLogged={async () => {
            setLoggingReply(false);
            await messagesQuery.reload();
          }}
        />
      )}

      {thanking && (
        <ThankYouModal
          partner={partner}
          referral={thanking}
          template={(templatesQuery.data ?? []).find(
            (row) => row.kind === "thank_you" && row.active,
          )}
          repFirstName={displayName.split(" ")[0] ?? ""}
          onClose={() => setThanking(null)}
          onSent={async () => {
            setThanking(null);
            await reload();
          }}
        />
      )}

      {referring && (
        <ReferralModal
          partner={partner}
          direction={referring}
          onClose={() => setReferring(null)}
          onDone={async () => {
            setReferring(null);
            await reload();
          }}
        />
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- pieces -- */

/**
 * Save them to the phone's address book.
 *
 * A blob URL and a synthetic click, because there is no server route to hand
 * back a file and a data: URI of a vCard is opened rather than saved by iOS.
 */
function downloadVcard(partner: Partner): void {
  const blob = new Blob([partnerVcard(partner)], { type: "text/vcard" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${partnerDisplayName(partner).replace(/\s+/g, "-")}.vcf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next tick: revoking immediately races the download on
  // Safari and produces a file the phone cannot open.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function AskAgain({ onDone }: { onDone: (note: string) => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");

  if (!open) {
    return (
      <button className="btn-small" onClick={() => setOpen(true)}>
        They said yes
      </button>
    );
  }

  return (
    <div className="field full">
      <label htmlFor="askagain">Where and when they said it</label>
      <input
        id="askagain"
        value={note}
        placeholder="Chamber lunch 9/3, in person"
        onChange={(e) => setNote(e.target.value)}
      />
      <div className="rowacts">
        <button
          className="btn-small"
          disabled={note.trim() === ""}
          onClick={() => void onDone(note.trim()).then(() => setOpen(false))}
        >
          Record it
        </button>
        <button className="btn-small" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function TextNowModal({
  partner,
  templates,
  repFirstName,
  referralsFromCount,
  referralsToCount,
  onClose,
  onSent,
}: {
  partner: Partner;
  templates: {
    id: string;
    name: string;
    body: string;
    category: string | null;
  }[];
  repFirstName: string;
  referralsFromCount: number;
  referralsToCount: number;
  onClose: () => void;
  onSent: () => Promise<void>;
}) {
  const { profileId } = useSession();
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const eligible = templates.filter(
    (template) =>
      template.category === null || template.category === partner.category,
  );
  const warning = sendWindowWarning(new Date());

  function applyTemplate(templateId: string): void {
    const template = eligible.find((row) => row.id === templateId);
    if (!template) return;
    setBody(
      renderPartnerMessage(
        template.body,
        mergeValues({
          partner,
          repFirstName,
          referralsFromCount,
          referralsToCount,
        }),
      ),
    );
  }

  async function go(): Promise<void> {
    setSending(true);
    setError(null);
    try {
      // Recorded before Messages opens, not after: navigating away is the last
      // thing this page does, and a write queued behind it does not happen.
      const message = await insertMessage({
        partner_id: partner.id,
        kind: "manual",
        body: body.trim(),
        status: "approved",
      });
      await markSent(message.id, profileId);
      await onSent();

      const outcome = send(SEND_CHANNEL, partner.phone_mobile, body.trim());
      if (outcome.state === "refused") setError(outcome.reason);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSending(false);
    }
  }

  return (
    <Modal
      title={`Text ${partner.first_name ?? partnerDisplayName(partner)}`}
      subtitle={`Opens Messages on your phone. It goes from your number, not Roof Board's.`}
      onClose={onClose}
      actions={
        <>
          <button className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={body.trim() === "" || sending}
            onClick={() => void go()}
          >
            Open Messages
          </button>
        </>
      }
    >
      {warning && <div className="send-warning">{warning}</div>}

      {eligible.length > 0 && (
        <div className="field full">
          <label htmlFor="tpl">Start from a template</label>
          <select
            id="tpl"
            defaultValue=""
            onChange={(e) => applyTemplate(e.target.value)}
          >
            <option value="">Write it myself</option>
            {eligible.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="field full">
        <label htmlFor="msgbody">Message</label>
        <textarea
          id="msgbody"
          rows={6}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <span className="sub">
          {body.trim().length} characters · {smsSegments(body)}{" "}
          {smsSegments(body) === 1 ? "segment" : "segments"}
        </span>
      </div>

      <ErrorNote error={error} />
    </Modal>
  );
}

/**
 * Log a reply.
 *
 * Replies land on the rep's own phone, which this application has no access to
 * and will not have (ADR 0067). So it is typed in, and the reports that count
 * replies per template say plainly that they undercount.
 */
function LogReplyModal({
  partnerId,
  onClose,
  onLogged,
}: {
  partnerId: string;
  onClose: () => void;
  onLogged: () => Promise<void>;
}) {
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function save(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      await insertMessage({
        partner_id: partnerId,
        direction: "inbound",
        kind: "reply",
        body: body.trim(),
        status: "logged",
      });
      await onLogged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  }

  return (
    <Modal
      title="Log a reply"
      subtitle="What they sent back, roughly. It only has to be enough to remember."
      onClose={onClose}
      actions={
        <>
          <button className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={body.trim() === "" || saving}
            onClick={() => void save()}
          >
            Log it
          </button>
        </>
      }
    >
      <div className="field full">
        <label htmlFor="replybody">They said</label>
        <textarea
          id="replybody"
          rows={4}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </div>
      <ErrorNote error={error} />
    </Modal>
  );
}

/**
 * One referral, and the two things a person does to it.
 *
 * The outcome is editable here because a referral with no job attached has
 * nothing to follow — the ones that came in through a job keep themselves up
 * to date (`referrals_follow_the_job`), and this is for the rest.
 *
 * The thank-you is the part that matters. A partner who sends work and hears
 * nothing back sends work once; the whole feature is built on the opposite of
 * that, and this is the two-tap version of it.
 */
function ReferralRow({
  referral,
  partner,
  thanked,
  onThank,
  onOutcome,
}: {
  referral: PartnerReferral;
  partner: Partner;
  thanked: boolean;
  onThank: () => void;
  onOutcome: (outcome: PartnerReferral["outcome"]) => Promise<void>;
}) {
  const owed =
    referral.direction === "from_partner" &&
    referral.outcome === "won" &&
    !thanked &&
    !whyNotTextable(partner);

  return (
    <li>
      <span className={`pill ${referral.direction}`}>
        {referral.direction === "from_partner" ? "→ us" : "→ them"}
      </span>
      <b>{referral.customer_name ?? "Somebody"}</b>
      <span className="sub">{when(referral.created_at)}</span>
      {referral.notes && <p className="sub">{referral.notes}</p>}

      <div className="rowacts">
        <select
          value={referral.outcome}
          disabled={Boolean(referral.job_id)}
          title={
            referral.job_id
              ? "This one follows its job — it changes when the job does."
              : "How it went"
          }
          onChange={(e) =>
            void onOutcome(e.target.value as PartnerReferral["outcome"])
          }
        >
          <option value="open">Open</option>
          <option value="contacted">Contacted</option>
          <option value="won">Sold</option>
          <option value="lost">Lost</option>
        </select>

        {owed && (
          <button className="btn-small" onClick={onThank}>
            Say thank you
          </button>
        )}
        {referral.direction === "from_partner" &&
          referral.outcome === "won" &&
          thanked && <span className="sub">Thanked</span>}
      </div>
    </li>
  );
}

/**
 * The text that goes out when a partner's referral signs.
 *
 * Short on purpose, and it asks for nothing. A thank-you with a request
 * attached to it is not a thank-you, and the next referral is the thing being
 * bought here.
 */
function ThankYouModal({
  partner,
  referral,
  template,
  repFirstName,
  onClose,
  onSent,
}: {
  partner: Partner;
  referral: PartnerReferral;
  template: MessageTemplate | undefined;
  repFirstName: string;
  onClose: () => void;
  onSent: () => Promise<void>;
}) {
  const { profileId } = useSession();
  const [body, setBody] = useState(() =>
    template
      ? renderPartnerMessage(
          template.body,
          mergeValues({
            partner,
            repFirstName,
            customerName: referral.customer_name ?? "",
          }),
        )
      : "",
  );
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  async function go(): Promise<void> {
    setSending(true);
    setError(null);
    try {
      const message = await insertMessage({
        partner_id: partner.id,
        kind: "thank_you",
        template_id: template?.id ?? null,
        body: body.trim(),
        status: "approved",
      });
      await markSent(message.id, profileId);
      await onSent();

      const outcome = send(SEND_CHANNEL, partner.phone_mobile, body.trim());
      if (outcome.state === "refused") setError(outcome.reason);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSending(false);
    }
  }

  return (
    <Modal
      title="Say thank you"
      subtitle={`${referral.customer_name ?? "Their referral"} signed. This goes from your phone.`}
      onClose={onClose}
      actions={
        <>
          <button className="btn-ghost" onClick={onClose}>
            Not now
          </button>
          <button
            className="btn-primary"
            disabled={body.trim() === "" || sending}
            onClick={() => void go()}
          >
            Open Messages
          </button>
        </>
      }
    >
      <div className="field full">
        <label htmlFor="thankbody">Message</label>
        <textarea
          id="thankbody"
          rows={4}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <span className="sub">
          {body.trim().length} characters · {smsSegments(body)}{" "}
          {smsSegments(body) === 1 ? "segment" : "segments"}
        </span>
      </div>
      <ErrorNote error={error} />
    </Modal>
  );
}

/**
 * Correcting what is on file.
 *
 * Not an afterthought: a partner saved with a mistyped mobile number, or with
 * none because the card only carried an office line, is a dead end without
 * this — they cannot be texted and there is nowhere to fix it. Which is exactly
 * the state the card reader's "degrade to a blank, never to a wrong value" rule
 * deliberately produces.
 *
 * Only the fields that decide whether a text can be sent and where it lands.
 * The rest of the card is the card's, and the photograph is beside it.
 */
function EditContact({
  partner,
  network,
  onClose,
  onSaved,
}: {
  partner: Partner;
  network: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [mobile, setMobile] = useState(formatE164(partner.phone_mobile) ?? "");
  const [office, setOffice] = useState(formatE164(partner.phone_office) ?? "");
  const [email, setEmail] = useState(partner.email ?? "");
  const [zip, setZip] = useState(partner.zip ?? "");
  const [county, setCounty] = useState(partner.county);
  const [category, setCategory] = useState(partner.category);
  const [lineOfWork, setLineOfWork] = useState(partner.line_of_work ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mobileBad = mobile.trim() !== "" && toE164(mobile) === null;
  const officeBad = office.trim() !== "" && toE164(office) === null;

  async function save(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      await updatePartner(partner.id, {
        phone_mobile: toE164(mobile),
        phone_office: toE164(office),
        email: email.trim() || null,
        zip: zip.trim() || null,
        county,
        category,
        line_of_work: normalizeLineOfWork(lineOfWork),
      });
      await onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  }

  return (
    <div className="fieldgrid edit-contact">
      <div className={`field full${mobileBad ? " tocheck" : ""}`}>
        <label htmlFor="e-mobile">
          {network ? "Mobile — the number texts go to" : "Mobile"}
        </label>
        <input
          id="e-mobile"
          type="tel"
          value={mobile}
          onChange={(e) => setMobile(e.target.value)}
        />
        {mobileBad && (
          <span className="sub">That is not a whole US number yet.</span>
        )}
      </div>

      <div className={`field${officeBad ? " tocheck" : ""}`}>
        <label htmlFor="e-office">Office</label>
        <input
          id="e-office"
          type="tel"
          value={office}
          onChange={(e) => setOffice(e.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor="e-email">Email</label>
        <input
          id="e-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor="e-zip">ZIP</label>
        <input
          id="e-zip"
          value={zip}
          onChange={(e) => {
            setZip(e.target.value);
            // The county follows the ZIP, until somebody overrides it below.
            setCounty(countyForZip(e.target.value));
          }}
        />
      </div>

      <div className="field">
        <label htmlFor="e-county">County</label>
        <select
          id="e-county"
          value={county}
          onChange={(e) => setCounty(e.target.value as Partner["county"])}
        >
          {PARTNER_COUNTIES.map((value) => (
            <option key={value} value={value}>
              {countyLabel(value)}
            </option>
          ))}
        </select>
      </div>

      <LineOfWorkField
        id="e-line"
        value={lineOfWork}
        onChange={setLineOfWork}
        onCategory={setCategory}
      />

      {network && (
        <div className="field">
          <label htmlFor="e-category">Category, for texts</label>
          <select
            id="e-category"
            value={category}
            onChange={(e) => setCategory(e.target.value as Partner["category"])}
          >
            {PARTNER_CATEGORIES.map((value) => (
              <option key={value} value={value}>
                {partnerCategoryLabel(value)}
              </option>
            ))}
          </select>
        </div>
      )}

      <ErrorNote error={error} />

      <div className="rowacts">
        <button
          className="btn-small"
          disabled={saving || mobileBad || officeBad}
          onClick={() => void save()}
        >
          Save
        </button>
        <button className="btn-small" onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
}
